import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Autocomplete, Box, Checkbox, CircularProgress, TextField, Typography } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';

import PositionStore from '../../../stores/position-store';
import { ILocation } from '../../../types/location';

export interface LocationFilterProps {
    nodeId: number;
    selectedLocationIds?: number[];
    onChange: (locationIds: number[]) => void;
}

const LocationFilter = observer(({ nodeId, selectedLocationIds, onChange }: LocationFilterProps) => {
    const [store] = useState(() => new PositionStore());
    const [selectedItems, setSelectedItems] = useState<ILocation[]>([]);
    const initRef = useRef(selectedLocationIds ?? []);
    const preselectedRef = useRef(false);

    useEffect(() => {
        setSelectedItems([]);
        store.load({ kind: 'node', nodeId }).then(() => {
            if (!preselectedRef.current && initRef.current.length > 0) {
                const preSelected = store.items.filter((l) => initRef.current.includes(l.location_id));
                if (preSelected.length > 0) {
                    setSelectedItems(preSelected);
                    preselectedRef.current = true;
                }
            }
        }).catch(() => {});
    }, [nodeId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (_: React.SyntheticEvent, newValue: ILocation[]) => {
        setSelectedItems(newValue);
        onChange(newValue.map((l) => l.location_id));
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={store.items}
            value={selectedItems}
            onChange={handleChange}
            loading={store.loading}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(opt, val) => opt.location_id === val.location_id}
            noOptionsText="Нет локаций"
            size="small"
            sx={{ mx: 1, mb: 1 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={selectedItems.length === 0 ? 'Все' : undefined}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {store.loading && <CircularProgress size={14} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.location_id}>
                    <Checkbox size="small" checked={selected} disableRipple sx={{ p: 0, mr: 1 }} />
                    <PlaceIcon sx={{ fontSize: 14, mr: 0.5, flexShrink: 0, color: 'text.secondary' }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{option.name}</Typography>
                        {option.address && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {option.address}
                            </Typography>
                        )}
                    </Box>
                </li>
            )}
        />
    );
});

export default LocationFilter;
